/**
 * The identifier types selectable for an unlisted supplier request
 * (USt-IdNr., Handelsregister A/B, GLN). Field names match
 * SupplierRequestCreateBody's `vatId` / `hra` / `hrb` / `gln`.
 */
export const SUPPLIER_IDENTIFIER_TYPES = [
  'vatId',
  'hra',
  'hrb',
  'gln',
] as const;

export const SUPPLIER_ACTIVATION_STATUS = [
  'completely_active',
  'partially_active',
  'activation_possible',
  'integration_planned',
] as const;

export const GLOBAL_SUPPLIER_STATUS = [
  'planned',
  'supplier_verifies',
  'in_progress',
  'not_feasible',
  'qr_code',
  'activatable',
] as const;

export const ACTIVATION_FEATURE_TYPE = ['dln', 'invoice'] as const;

export const CONTACT_LINK_STATUS = ['expired', 'created', 'filled'] as const;

export const SUPPLIER_ACTIVATION_RESPOND_ACTION = [
  'confirm',
  'decline',
] as const;

export const SUPPLIER_CONTACT_ROLE = [
  'dln_contact',
  'invoice_contact',
  'contract_contact',
  'calloff_contact',
  'claim_manager',
  'quality_contact',
] as const;

export const VISIBILITY_SCOPE = ['company', 'company_account'] as const;

export const VOLUME_BUCKET = [
  '0-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '>5000',
] as const;

/**
 * Status of the request for adding a new supplier to the VESTIGAS supplier network.
 */
export const SUPPLIER_REQUEST_STATE = [
  'pending', // Request submitted, awaiting review
  'created_new_company', // Request resolved by creating a new company (terminal)
  'linked_to_existing_company', // Request resolved by linking to an existing company (terminal)
  'rejected', // Request rejected (terminal)
] as const;

/**
 * Outcome of GET /suppliers/requests/check — whether a submitted identifier
 * already exists as a supplier or an in-flight request.
 */
export const SUPPLIER_IDENTIFIER_CHECK_OUTCOME = [
  'not_found', // No matching supplier or pending request
  'duplicate', // Identifier matches an existing supplier
  'blocked', // Identifier matches a blacklisted company
  'pending_duplicate', // Caller already has a pending request for this identifier
] as const;

/**
 * Status of the request for activating the exchange of data between VESTIGAS
 * and a supplier within the VESTIGAS supplier network.
 */
export const ACTIVATION_REQUEST_STATE = [
  'requested', // Magic-link email dispatched to supplier, awaiting response
  'confirmed', // Supplier confirmed via magic link
  'declined', // Supplier declined via magic link (terminal)
  'canceled', // Customer canceled before terminal state (terminal)
  'expired', // Magic link expired without supplier response (terminal)
  'successful', // First DLN/invoice received, activation complete (terminal)
  'draft', // Customer is preparing the request, not yet sent
  'error', // Email delivery failed or unrecoverable system error (terminal)
] as const;

/**
 * Per-(supplier, customer, channel) activation snapshot returned on the supplier
 * card via `activation_status_dln` / `activation_status_invoice`.
 */
export const RELATION_ACTIVATION_STATUS = [
  'active',
  'confirmed',
  'declined',
  'draft',
  'requested',
] as const;

export const SUPPLIER_HISTORY_ACTION = [
  'activation_canceled',
  'activation_confirmed',
  'activation_created',
  'activation_declined',
  'activation_dln_received',
  'activation_error',
  'activation_expired',
  'activation_magic_link_sent',
  'activation_requested',
  'activation_successful',
  'contact_added',
  'contact_email_opt_out',
  'contact_removed',
  'contact_updated',
  'created',
  'relationship_updated',
  'responsible_person_updated',
  'status_changed',
  'status_changed_status_dln', // deprecated; combined status is reported in status_changed
  'status_changed_status_invoice', // deprecated; combined status is reported in status_changed
  'supplier_request_submitted',
  'supplier_request_validated',
  'updated',
] as const;
